import { Opcion, OpcionSitio } from '../../../models/catalogo.type'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { TRecordatorioForm, schemaRecordatorios } from '../../recordatorios/models/recordatorioForm.type'

export type TAutorizacionForm = {
  idSitio: OpcionSitio
  idContactoSitio: Opcion | null
  nombreSitio: string
  numero: string
  autorizacion: Opcion
  esIndefinida: boolean
  estatus: Opcion | null
  cantidadDuracion: number | null
  unidadDuracion: Opcion | null
  competencia: Opcion
  dependencia: string
  fechaEmision: string
  fechaVencimiento: string | null
  fechaUltimaRenovacion: string | null
  recordatorios: TRecordatorioForm[] | []
  esPorFechaVencimiento: boolean
}

/**
 * Valida las dos formas funcionales de capturar una autorizacion:
 * por fecha de vencimiento explicita o por cantidad/unidad de duracion.
 * Las autorizaciones indefinidas omiten esas reglas condicionales.
 */
const schema = yup.object({
  idSitio: yup.object().nullable().required('El sitio es requerido'),
  idContactoSitio: yup.object().nullable().required('El contacto es requerido'),
  numero: yup.string().max(100).trim().required('El número es requerido'),
  autorizacion: yup.object().nullable().required('La autorización es requerida'),
  esIndefinida: yup.boolean().default(false),
  estatus: yup
    .object()
    .nullable()
    .when(['esIndefinida'], {
      is: false,
      then: yup.object().nullable().required('El estatus es requerido'),
    }),
  competencia: yup.object().nullable().required('La competencia es requerida'),
  dependencia: yup.string().max(50).trim().required('La dependencia es requerida'),
  fechaUltimaRenovacion: yup.string().trim().nullable(),
  fechaEmision: yup.string().trim().nullable().required('La fecha es requerida'),
  isSeleccionFechaVencimiento: yup.boolean().default(false),
  cantidadDuracion: yup
    .number()
    .nullable()
    .when(['esPorFechaVencimiento', 'esIndefinida'], {
      is: (esPorFechaVencimiento: boolean, esIndefinida: boolean) => {
        return !esPorFechaVencimiento && !esIndefinida
      },
      then: yup
        .number()
        .nullable()
        .required('Cantidad es requerida')
        .typeError('La cantidad requiere de un número')
        .min(1, 'La cantidad debe ser mayor a cero'),
    }),
  unidadDuracion: yup
    .object()
    .nullable()
    .when(['esPorFechaVencimiento', 'esIndefinida'], {
      is: (esPorFechaVencimiento: boolean, esIndefinida: boolean) => {
        return !esPorFechaVencimiento && !esIndefinida
      },
      then: yup.object().nullable().required('La unidad es requerida'),
    }),
  fechaVencimiento: yup
    .string()
    .trim()
    .nullable()
    .when(['esPorFechaVencimiento', 'esIndefinida'], {
      is: (esPorFechaVencimiento: boolean, esIndefinida: boolean) => {
        return esPorFechaVencimiento && !esIndefinida
      },
      then: yup.string().trim().nullable().required('La fecha es requerida'),
    }),
})
const schemaConcat = schema.concat(schemaRecordatorios)

/**
 * Inicializa el formulario de autorizacion con valores seguros para las ramas
 * condicionales de vigencia y con arreglo de recordatorios listo para la UI.
 */
export const useAutorizacionForm = () => {
  return useForm<TAutorizacionForm>({
    mode: 'all',
    resolver: yupResolver(schemaConcat),
    defaultValues: {
      esIndefinida: false,
      esPorFechaVencimiento: false,
      recordatorios: [],
    },
  })
}
